/**
 * The Settings rows piece 24 seeds, spelled once so the seed and every reader
 * name a declared key (piece 24, the Settings entries).
 *
 * 🔨 One row per key at Organization scope. The list grows as the piece's
 * stages land — the mileage rate, the meal threshold, the match confidence
 * tiers, the card surcharge, the 1099 threshold and the inbox address are each
 * declared by the piece and join here with the stage that builds them.
 */

import { BILL_VARIANCE_TOLERANCE_DEFAULT, BILL_VARIANCE_TOLERANCE_KEY } from '../documents/billVariance';
import { CARD_SURCHARGE_PERCENT_DEFAULT, CARD_SURCHARGE_PERCENT_KEY } from './cardSurcharge';
import { CHECK_NUMBERING_DIRECTION_DEFAULT, CHECK_NUMBERING_DIRECTION_KEY } from './checkBatch';
import {
  MATCH_AMOUNT_TOLERANCE_DEFAULT, MATCH_AMOUNT_TOLERANCE_KEY, MATCH_NAME_SIMILARITY_DEFAULT, MATCH_NAME_SIMILARITY_KEY, MATCH_WINDOW_DAYS_DEFAULT, MATCH_WINDOW_DAYS_KEY,
} from './bankMatching';
import { MEAL_THRESHOLD_DEFAULT_CENTS, MEAL_THRESHOLD_KEY, MILEAGE_RATE_DEFAULT_CENTS, MILEAGE_RATE_KEY } from './expenses';
import { FORM_1099_THRESHOLD_DEFAULT_CENTS, FORM_1099_THRESHOLD_KEY } from './vendorTax';

export interface MoneyOutSettingRow {
  key: string;
  /** null seeds a JSON null: a row whose value a person names, never the seed. */
  value: string | number | null;
}

/** Piece 24's seeded rows: the bill variance tolerance at 2 percent (decision 0224, settled 2026-09-14). */
export const PIECE_24_SETTINGS: readonly MoneyOutSettingRow[] = [
  { key: BILL_VARIANCE_TOLERANCE_KEY, value: BILL_VARIANCE_TOLERANCE_DEFAULT },
  // 🔨 Effective-dated: the threshold a tax year reads is the row in force at that year's end, so a 2027 run
  // prepared in 2029 uses the 2027 figure. Seeded 2,000 dollars in cents (piece 24, Filing Status).
  { key: FORM_1099_THRESHOLD_KEY, value: FORM_1099_THRESHOLD_DEFAULT_CENTS },
  // The three tiers a bookkeeper tunes, so retuning what counts as a High match is a settings change and never a release.
  { key: MATCH_WINDOW_DAYS_KEY, value: MATCH_WINDOW_DAYS_DEFAULT },
  { key: MATCH_AMOUNT_TOLERANCE_KEY, value: MATCH_AMOUNT_TOLERANCE_DEFAULT },
  { key: MATCH_NAME_SIMILARITY_KEY, value: MATCH_NAME_SIMILARITY_DEFAULT },
  // The meal threshold a per-person figure is measured against, and the mileage rate a line stamps at entry.
  { key: MEAL_THRESHOLD_KEY, value: MEAL_THRESHOLD_DEFAULT_CENTS },
  // 🔨 Effective-dated: a line takes the rate in force on the day the travel happened, never the row current when it is typed.
  { key: MILEAGE_RATE_KEY, value: MILEAGE_RATE_DEFAULT_CENTS },
  // Decision 0556: which way the numbers run down a printed stack of checks, since a printer that needs the pages flipped runs backwards.
  { key: CHECK_NUMBERING_DIRECTION_KEY, value: CHECK_NUMBERING_DIRECTION_DEFAULT },
  // Decisions 0239 and 1620: a credit card's surcharge, seeded at the networks' cap of 3; debit and prepaid hold no row.
  { key: CARD_SURCHARGE_PERCENT_KEY, value: CARD_SURCHARGE_PERCENT_DEFAULT },
];

/** The declared keys; a reader naming a key outside this list names nothing this piece owns. */
export const PIECE_24_SETTING_KEYS: readonly string[] = PIECE_24_SETTINGS.map((row) => row.key);

export function isDeclaredMoneyOutSetting(key: string): boolean {
  return PIECE_24_SETTING_KEYS.includes(key);
}
